import { graphql } from '../../../gql'

export const readMyselfQuery = graphql(`
  query ReadMyself {
    readMyself {
      id
      firstName
      surname
      email
      status
      birthday
      street
      zip
      city
      phone
      isDebugUser
      isInTargetGroup
      isInTargetGroupAndConfirmed
      requiresProfileUpdate
      requiredNotificationPermissions
      profileImageUrl
      customerLogoUrl
      currentFestivalId
      notificationGeneral
      notificationMarketing
      notificationBirthday
      notificationInvitations
      notificationSupport
      acceptScheduleAgreement
      currentSeasonCheckin {
        id
        arrivalDate
        arrivalTime
        departureDate
        departureTime
        acceptScheduleAgreement
      }
      navigationItems {
        name
        href
        icon
        permissions
      }
      memberships {
        ...allMembershipDetails
      }
      availabilitySlots {
        id
        begin
        end
        sectionRole {
          id
          title
        }
      }
      acceptedSlots {
        id
        begin
        end
        isFlexSlot
        sectionRole {
          id
          title
          shiftExemption
          autoSelfCheckoutEnabled
        }
      }
      acceptedSlotLeads {
        id
        begin
        end
        sectionRole {
          id
          title
          section {
            id
          }
        }
      }
      sectionLeads {
        id
      }
      sectionEditPermissions {
        id
      }
      sections {
        id
        title
        season
      }
      sectionRoles {
        id
        title
        season
        shiftExemption
      }
      permissions
    }
  }
`)

export const readOneMembershipQuery = graphql(`
  query ReadOneMembership($input: ReadOneMembershipInput!) {
    readOneMembership(input: $input) {
      id
      status
      emptyRequiredNotificationFields
      statusLabels {
        id
        title
        color
      }
      answers {
        id
        propertyID
        propertyType
        value
      }
      userGroup {
        id
        title
        welcomeMessage
        footnote
        isReadonly
        statusLabels {
          id
          title
          color
        }
        properties {
          id
          title
          infotext
          type
          shorten
          isRequired
          isReadonly
          isPrivate
          propertyOptions {
            id
            title
            description
            limit
            disabled
          }
        }
      }
    }
  }
`)

export const readStorageAdministrationQuery = graphql(`
  query ReadStorageAdministration {
    readStorageAdministration {
      id
      title
      description
      imageUrl
      customer {
        id
      }
      storagePlaces {
        id
        title
        description
        imageUrl
        itemInventories {
          id
          title
          description
          amount
          imageUrl
          articleTemplate {
            id
            title
          }
        }
      }
    }
  }
`)

export const readArticleTemplatesQuery = graphql(`
  query ReadArticleTemplates {
    readArticleTemplates {
      id
      title
      description
      imageUrl
    }
  }
`)

export const readAllUsersQuery = graphql(`
  query ReadAllUsers {
    readAllUsers {
      id
      firstName
      surname
      email
      memberships {
        id
        status
        userGroup {
          id
          title
        }
      }
    }
  }
`)

export const readAllUserNamesQuery = graphql(`
  query ReadAllUserNames($input: ReadAllUsersInput) {
    readAllUsers(input: $input) {
      id
      firstName
      surname
    }
  }
`)

export const readAllGroupsQuery = graphql(`
  query ReadAllGroups {
    readAllGroups {
      id
      title
      membershipAmount
    }
  }
`)

export const readAllBingoGamesQuery = graphql(`
  query ReadAllBingoGames {
    readAllBingoGames {
      id
      title
      bingoGameItems {
        id
        title
        artist
        sort
      }
    }
  }
`)

export const readOneGroupQuery = graphql(`
  query ReadOneGroup($input: ReadOneGroupInput!) {
    readOneGroup(input: $input) {
      ...oneGroupDetails
      title
      properties {
        id
        title
        type
        shorten
        isRequired
        isReadonly
        isPrivate
        propertyOptions {
          id
          title
          description
        }
      }
    }
  }
`)

export const readOneBingoGameQuery = graphql(`
  query ReadOneBingoGame($id: ID!) {
    readOneBingoGame(filter: {id: {eq: $id}}) {
      id
      title
      bingoGameItems {
        id
        title
        artist
        sort
      }
    }
  }
`)

export const readBingoGameCardsQuery = graphql(`
  query ReadBingoGameCards($input: ReadBingoGameCardsInput!) {
    readBingoGameCards(input: $input)
  }
`)

export const readStatusLabelsByGroupQuery = graphql(`
  query ReadStatusLabelsByGroup($input: StatusLabelByGroupInput!) {
    readStatusLabelsByGroup(input: $input) {
      id
      title
      color
    }
  }
`)

export const readOneUserQuery = graphql(`
  query ReadOneUser($input: ReadOneUserInput!) {
    readOneUser(input: $input) {
      id
      ...userDetails
      currentSeasonCheckin {
        id
        arrivalDate
        arrivalTime
        departureDate
        departureTime
        checkinTime
        scheduleEmailStatus
      }
      acceptedSlots {
        id
        begin
        end
        isFlexSlot
        sectionRole {
          id
          title
          section {
            id
            title
          }
        }
      }
      acceptedSlotLeads {
        id
        begin
        end
        sectionRole {
          id
          title
          section {
            id
            title
          }
        }
      }
      sectionLeads {
        id
        title
        season
      }
      sections {
        id
        title
        season
      }
      sectionRoles {
        id
        title
        season
        shiftExemption
      }
      memberships {
        id
        status
        statusLabels {
          id
          title
          color
        }
        userGroup {
          id
          title
          statusLabels {
            id
            title
            color
          }
        }
      }
    }
  }
`)

export const readOneUsersAvailabilityQuery = graphql(`
  query ReadOneUsersAvailability($input: ReadOneUserInput!) {
      readOneUser(input: $input) {
          id
          availabilitySlots {
              id
              begin
              end
              sectionRole {
                  id
                  title
              }
          }
      }
  }
`)

export const readScheduleAgreementTextQuery = graphql(`
  query ReadScheduleAgreementText {
    readCurrentSeason {
      id
      scheduleAgreementText
    }
  }
`)

export const readScheduleStatisticQuery = graphql(`
  query ReadScheduleStatistic {
    readCurrentSeason {
      id
      userWithSections
      userInTargetGroup
      userWithAvailabilities
      userWithAcceptedSlots
      totalSlotsWithoutTemplate
      totalUnacceptedSlots
      totalSlotHours
      acceptedSlotHours
      userWithoutSlotsAmount
      totalCheckedInSlots
    }
  }
`)

export const readCurrentSeasonQuery = graphql(`
  query ReadCurrentSeason {
    readCurrentSeason {
      id
      title
      passBackgroundImageBase64
      shiftColor
      begin
      end
      isLatest
      constructionBegin
      constructionEnd
      dismantlingBegin
      dismantlingEnd
      scheduleActive
      lineupItems {
        id
        artist
        date
        begin
        end
      }
      sections {
        id
        title
        publicAccess
        canViewSchedule
        canEditSchedule
        sectionRoles {
          id
          title
          shiftExemption
        }
      }
      seasonCheckinItems {
        id
        title
      }
      crewInfoActive
      crewInfoText
    }
  }
`)

export const readOneSectionQuery = graphql(`
  query ReadOneSection($id: ID!) {
    readOneSection(filter: {id: {eq: $id}}) {
      id
      title
      publicAccess
      scheduleDates
      scheduleLeadDates
      canViewSchedule
      canEditSchedule
      sectionLeaders {
        id
        firstName
        surname
        phone
        email
        profileImageUrl
      }
      sectionRoles {
        id
        title
        shiftExemption
      }
    }
  }
`)

export const readOneSectionAndRolesQuery = graphql(`
  query ReadOneSectionAndRoles($id: ID!) {
    readOneSection(filter: {id: {eq: $id}}) {
      id
      title
      publicAccess
      canViewSchedule
      canEditSchedule
      scheduleDates
      scheduleLeadDates
      sectionLeaders {
        id
        firstName
        surname
        phone
        email
        profileImageUrl
      }
      sectionRoles {
        id
        title
        shiftExemption
        userAvailabilities {
          id
          firstName
          surname
          minorAge
          acceptedSlots {
            id
            begin
            end
            sectionRoleTitle
            sectionRoleHashID
          }
          acceptedSlotLeads {
            id
            begin
            end
            sectionRoleTitle
          }
          availabilitySlots {
            id
            begin
            end
            sectionRoleHashID
          }
        }
      }
      totalSectionSlotsToConfirm {
        id
        begin
        end
      }
    }
  }
`)

export const sectionSlotFragment = graphql(`
  fragment sectionSlot on SectionSlot {
    id
    acceptedUser {
      id
      firstName
      surname
    }
    rejectedUsers {
      id
      reason
    }
    sectionRole {
      id
    }
  }
`)

export const readSectionSlotsPerDateBySectionQuery = graphql(`
  query ReadSectionSlotsPerDateBySection($input: ReadSectionSlotsPerDateBySectionInput) {
    readSectionSlotsPerDateBySection(input: $input) {
      ...sectionSlot
      begin
      end
      note
      useAsTemplate
      autoConfirm
      isPrivate
      queueActive
      isFlexSlot
      deleteOnReject
      checkinTime
    }
  }
`)

export const sectionLeadSlotFragment = graphql(`
  fragment sectionSlotLead on SectionSlotLead {
    id
    acceptedUser {
      id
      firstName
      surname
    }
  }
`)

export const readSectionSlotsByRoleQuery = graphql(`
  query ReadSectionSlotsByRole($input: ReadSectionSlotsByRoleInput) {
    readSectionSlotsByRole(input: $input) {
      ...sectionSlot
      begin
      end
      note
      useAsTemplate
      autoConfirm
      isPrivate
      queueActive
      isFlexSlot
      deleteOnReject
      checkinTime
    }
  }
`)

export const readSectionLeadSlotsByRoleQuery = graphql(`
  query ReadSectionLeadSlotsByRole($input: ReadSectionLeadSlotsByRoleInput) {
    readSectionLeadSlotsByRole(input: $input) {
      ...sectionSlotLead
      begin
      end
      note
    }
  }
`)

export const readSectionLeadSlotsPerDateBySectionQuery = graphql(`
  query ReadSectionLeadSlotsPerDateBySection($input: ReadSectionLeadSlotsPerDateBySectionInput) {
    readSectionLeadSlotsPerDateBySection(input: $input) {
      ...sectionSlotLead
      begin
      end
      note
      sectionRole {
        id
        section {
          id
        }
      }
    }
  }
`)

export const readOneSectionRoleQuery = graphql(`
  query ReadOneSectionRole($roleID: ID!) {
    readOneSectionRole(filter: {id: {eq: $roleID}}) {
      id
      title
      userAvailabilities {
        id
        firstName
        surname
        minorAge
        acceptedSlots {
          id
          begin
          end
          sectionRole {
            id
            title
          }
        }
        availabilitySlots {
          id
          begin
          end
          sectionRole {
            id
          }
        }
      }
    }
  }
`)

export const readOneSectionRoleLeadsQuery = graphql(`
  query ReadOneSectionRoleLeads($roleID: ID!) {
    readOneSectionRole(filter: {id: {eq: $roleID}}) {
      id
      title
      userAvailabilities {
        id
        firstName
        surname
        minorAge
        acceptedSlots {
          id
          begin
          end
          sectionRole {
            id
            title
          }
        }
        acceptedSlotLeads {
          id
          begin
          end
          sectionRoleTitle
        }
        availabilitySlots {
          id
          begin
          end
          sectionRole {
            id
          }
        }
      }
      sectionSlotLeads {
        id
        begin
        end
      }
    }
  }
`)

export const readAvailableGroupsQuery = graphql(`
  query ReadAvailableGroups($input: ReadAvailableGroupsInput!) {
    readAvailableGroups(input: $input) {
      id
      title
      longTitle
      imageUrl
    }
  }
`)

export const readSeasonsQuery = graphql(`
  query ReadSeasons {
    readSeasons {
      id
      title
      begin
      end
      isLatest
    }
  }
`)

export const readCurrentSectionsAndRolesQuery = graphql(`
  query ReadCurrentSectionsAndRoles {
    readCurrentSeason {
      id
      sections {
        id
        title
        sectionRoles {
          id
          title
          description
        }
      }
    }
  }
`)

export const readSeasonSectionsOverviewQuery = graphql(`
  query ReadSeasonSectionsOverview {
    readCurrentSeason {
      id
      title
      sections {
        id
        title
        publicAccess
        canViewSchedule
        canEditSchedule
        sectionRoles {
          id
          title
          totalSectionSlots
          totalAcceptedSectionSlots
          totalPotentialSectionSlotsToConfirm
        }
      }
    }
  }
`)

export const readSectionRolesOfOneSectionQuery = graphql(`
  query ReadSectionRolesOfOneSection($id: ID!) {
    readOneSection(filter: {id: {eq: $id}}) {
      id
      title
      sectionRoles {
        id
        title
        description
      }
    }
  }
`)

export const readUserNotesQuery = graphql(`
  query ReadUserNotes($input: ReadUserNotesInput!) {
    readUserNotes(input: $input) {
      id
      created
      author {
        id
        firstName
        surname
      }
      message
    }
  }
`)

export const readDashboardSeasonQuery = graphql(`
  query ReadDashboardSeasonQuery {
    readCurrentSeason {
      id
      title
      isInTargetGroup
      targetGroupText
      targetGroupButtonText
    }
  }
`)

export const readUserSearchQuery = graphql(`
  query ReadUserSearch($input: ReadUserSearchInput!) {
    readUserSearch(input: $input) {
      id
      firstName
      surname
      profileImageUrl
    }
  }
`)

export const readSectionRolesForApplication = graphql(`
  query ReadSectionRolesForApplication {
    readSectionRolesForApplication {
      id
      title
      highPriority
      section {
        id
        title
      }
    }
  }
`)

export const readOneSectionRoleForApplication = graphql(`
  query ReadOneSectionRoleForApplication($roleID: ID!) {
    readOneSectionRole(filter: {id: {eq: $roleID}}) {
      id
      title
      description
      section {
        id
        sectionLeaders {
          id
          firstName
          surname
          phone
          email
          profileImageUrl
        }
      }
      sectionSlots {
        id
        begin
        end
      }
      emptySectionSlots {
        id
        begin
        end
        organisation
      }
    }
  }
`)

export const readMyAvailabilitiesQuery = graphql(`
  query ReadMyAvailabilites {
    readMyself {
      id
      availabilitySlots {
        id
        begin
        end
        sectionRole {
          id
          title
        }
      }
    }
  }
`)

export const readUserAvailabilitiesQuery = graphql(`
  query ReadUserAvailabilities($input: ReadOneUserInput!) {
    readOneUser(input: $input) {
      id
      availabilitySlots {
        id
        begin
        end
        sectionRole {
          id
          title
        }
      }
    }
  }
`)

export const readOneAvailabilitySlotQuery = graphql(`
  query ReadOneAvailabilitySlot($input: ReadOneAvailabilitySlotInput!) {
    readOneAvailabilitySlot(input: $input) {
      id
      begin
      end
    }
  }
`)

export const readGuestListForCurrentSeasonQuery = graphql(`
    query ReadGuestListForCurrentSeason($input: ReadGuestListForCurrentSeasonInput!) {
      readGuestListForCurrentSeason(input: $input) {
        ...guestListItemDetails
      }
    }
`)

export const readMissingSectionRoleLeadsQuery = graphql(`
  query ReadMissingSectionRoleLeadsByDate($input: ReadMissingSectionRoleLeadsByDateInput!) {
    readMissingSectionRoleLeadsByDate(input: $input) {
      id
      title
      section {
        id
        title
      }
    }
  }
`)

export const readEmptySectionRoleLeadsByDateQuery = graphql(`
  query ReadEmptySectionRoleLeadsByDate($input: ReadEmptySectionRoleLeadsByDateInput!) {
    readEmptySectionRoleLeadsByDate(input: $input) {
      id
      begin
      end
      sectionRole {
        id
        title
        section {
          id
          title
        }
      }
    }
  }
`)

export const readEmptySectionSlotsByDateQuery = graphql(`
  query ReadEmptySectionSlotsByDate($input: ReadEmptySectionSlotsByDateInput!) {
    readEmptySectionSlotsByDate(input: $input) {
      id
      begin
      end
      sectionRole {
        id
        title
        section {
          id
          title
        }
      }
    }
  }
`)

export const readCheckinByUserIDQuery = graphql(`
  query ReadCheckinByUserID($input: ReadCheckinByUserIDInput!) {
    readCheckinByUserID(input: $input) {
      id
      checkinTime
      user {
        id
        firstName
        surname
        birthday
        profileImageUrl
        hasShiftExemption
        targetGroupRegistrationDate
        targetGroupMembership {
          id
          statusLabels {
            id
            title
            color
          }
        }
        passes {
          id
          type
          checkinTime
          passID
        }
        acceptedSlots {
          id
          begin
          end
          sectionRole {
            id
            title
            section {
              id
            }
          }
        }
        acceptedSlotLeads {
          id
          begin
          end
          sectionRole {
            id
            title
            section {
              id
            }
          }
        }
      }
      seasonCheckinItems {
        id
        title
        checkinTime
      }
      checkinProperties {
        id
        title
        type
        propertyOptions {
          id
          title
        }
      }
      checkinPropertyAnswers {
        id
        propertyID
        value
      }
    }
  }
`)

export const readSectionUsersQuery = graphql(`
  query ReadSectionUsers($sectionID: ID) {
    readSectionUsers(sectionID: $sectionID) {
      id
      firstName
      surname
      hasShiftExemption
      userAvailabilityPeriods {
        userID
        date
        workload
      }
      availabilitySlots {
        id
        begin
        end
        sectionRole {
          id
          title
          section {
            id
          }
        }
      }
      acceptedSlots {
        id
        begin
        end
        sectionRole {
          id
          title
          section {
            id
          }
        }
      }
      acceptedSlotLeads {
        id
        begin
        end
        sectionRole {
          id
          title
          section {
            id
          }
        }
      }
      sectionRoles {
        id
        title
        shiftExemption
        section {
          id
          title
        }
        season
      }
    }
  }
`)

export const readUserMessagesQuery = graphql(`
  query ReadMyMessages {
    readMyself {
      id
      messagesSent {
        id
        title
        subject
        content
        summary
        recipientType
      }
    }
  }
`)

export const readOneMessageQuery = graphql(`
  query ReadOneMessage($messageID: ID!) {
    readOneMessage(filter: {id: {eq: $messageID}}) {
      id
      title
      subject
        content
        recipientType
        onlyConfirmedMembers
        notificationPermission
        recipientAmount
      finalized
      userGroups {
        id
        title
      }
      userRecipients {
        id
        firstName
        surname
      }
    }
  }
`)

export const readMessagesEmailRecipientsQuery = graphql(`
    query ReadMessagesEmailRecipients($messageID: ID!) {
      readMessagesEmailRecipients(messageID: $messageID) {
        id
        firstName
        surname
      }
    }
`)

export const readSectionUsersOfAllSectionsQuery = graphql(`
  query ReadSectionUsersOfAllSections($sectionID: ID) {
    readSectionUsers(sectionID: $sectionID) {
      id
      firstName
      surname
      hasShiftExemption
      userAvailabilityPeriods {
        userID
        date
        workload
      }
      availabilitySlots {
        id
        begin
        end
        sectionRole {
          id
          title
          section {
            id
          }
        }
      }
      acceptedSlots {
        id
        begin
        end
        isFlexSlot
        sectionRole {
          id
          title
          section {
            id
          }
        }
      }
      acceptedSlotLeads {
        id
        begin
        end
        sectionRole {
          id
          title
          section {
            id
          }
        }
      }
    }
  }
`)

export const readAllPassesQuery = graphql(`
    query ReadAllPasses {
      readAllPasses {
        id
        passID
        type
        organisation
        userType
        user {
          id
          firstName
          surname
        }
        firstName
        surname
        prepareTime
        checkinTime
      }
    }
`)

export const readOnePassQuery = graphql(`
    query ReadOnePass($passID: String!) {
      readOnePass(passID: $passID) {
        id
        passID
        type
        organisation
        userType
        user {
          id
          firstName
          surname
        }
        firstName
        surname
        prepareTime
        checkinTime
      }
    }
`)

export const readUserWithoutSlotsQuery = graphql(`
    query ReadUserWithoutSlots {
      readCurrentSeason {
        id
        userWithoutSlots {
          id
          firstName
          surname
          isInTargetGroup
          seasonNotice
          userAvailabilityPeriods {
            userID
            date
            workload
          }
          availabilitySlots {
            id
            begin
            end
            sectionRole {
              id
              title
              section {
                id
              }
            }
          }
        }
      }
    }
`)

export const readCrewInfoFileQuery = graphql(`
    query ReadCrewInfoFile {
      readCurrentSeason {
        id
        crewInfoFile
      }
    }
`)

export const readSectionSlotsByDayQuery = graphql(`
    query ReadSectionSlotsByDay($input: ReadSectionSlotsByDayInput) {
      readSectionSlotsByDay(input: $input) {
        id
        begin
        end
        checkinTime
        acceptedUser {
          id
          firstName
          surname
          profileImageUrl
        }
        sectionRole {
          id
          title
        }
      }
    }
`)

export const readMissingSectionSlotsByDayQuery = graphql(`
    query ReadMissedSectionSlotsByDay($input: ReadMissedSectionSlotsByDayInput) {
      readMissedSectionSlotsByDay(input: $input) {
        id
        begin
        end
        checkinTime
        acceptedUser {
          id
          firstName
          surname
          profileImageUrl
        }
        sectionRole {
          id
          title
        }
      }
    }
`)

export const readOneSectionSlotForCheckinQuery = graphql(`
    query ReadOneSectionSlotForCheckin($ID: String!) {
      readOneSectionSlot(ID: $ID) {
        id
        checkinTime
      }
    }
`)

export const readAllSeasonCheckinsQuery = graphql(`
    query ReadAllSeasonCheckins {
      readCurrentSeason {
        id
        allUserCheckins {
          id
          checkinTime
          user {
            id
            firstName
            surname
          }
          seasonCheckinItems {
            id
            title
            checkinTime
          }
        }
      }
    }
`)

export const readSeasonCheckinSettingsAdministrationQuery = graphql(`
  query ReadSeasonCheckinSettingsAdministration($seasonID: ID!) {
    readSeasonForAdministration(seasonID: $seasonID) {
      id
      checkinEmailActiveTime
      checkinEmailContent
      checkinProperties {
        id
        title
      }
      targetGroup {
        id
        properties {
          id
          title
        }
      }
    }
  }
`)

export const readSeasonCheckinItemsManagementQuery = graphql(`
  query ReadSeasonCheckinItemsManagement($seasonID: ID!) {
    readSeasonForAdministration(seasonID: $seasonID) {
      id
      seasonCheckinItems {
        id
        title
      }
    }
  }
`)

export const readSeasonCheckinItemsAdministrationQuery = graphql(`
  query ReadSeasonCheckinItemsAdministration {
    readCurrentSeason {
      id
      checkinEmailActiveTime
      checkinEmailContent
      checkinProperties {
        id
        title
      }
      targetGroup {
        id
        properties {
          id
          title
        }
      }
      allUserCheckins {
        id
        checkinProperties {
          id
          title
          propertyOptions {
            id
            title
          }
        }
      }
      seasonCheckinItems {
        id
        title
        type
        quantity
        issuedAmount
        remainingAmount
        property {
          id
          title
        }
        propertyOptionQuantities {
          id
          quantity
          issuedAmount
          requestedAmount
          propertyOption {
            id
            title
          }
        }
      }
      seasonCheckinItemGenericIssues {
        id
        title
        quantity
        created
        seasonCheckinItem {
          id
          title
        }
        seasonCheckinItemPropertyOptionQuantity {
          id
          propertyOption {
            id
            title
          }
        }
      }
    }
  }
`)

export const readSeasonSectionRoleGroupsAdministrationQuery = graphql(`
  query ReadSeasonSectionRoleGroupsAdministration {
    readCurrentSeason {
      id
      sections {
        id
        title
        sectionRoles {
          id
          title
        }
      }
      sectionRoleGroups {
        id
        title
        sectionRoles {
          id
          title
          section {
            id
            title
          }
        }
      }
    }
  }
`)

export const readSectionRoleGroupPrintTemplateQuery = graphql(`
  query ReadSectionRoleGroupPrintTemplate($input: ReadSectionRoleGroupPrintTemplateInput!) {
    readSectionRoleGroupPrintTemplate(input: $input)
  }
`)

export const readSeasonCrewInfoAdministrationQuery = graphql(`
  query ReadSeasonCrewInfoAdministration($seasonID: ID!) {
    readSeasonForAdministration(seasonID: $seasonID) {
      id
      crewInfoActive
      crewInfoTextRaw
    }
  }
`)

export const readSeasonCrewInfoFileQuery = graphql(`
  query ReadSeasonCrewInfoFile($seasonID: ID!) {
    readSeasonForAdministration(seasonID: $seasonID) {
      id
      crewInfoFile
    }
  }
`)

export const readSeasonScheduleEmailAdministrationQuery = graphql(`
  query ReadSeasonScheduleEmailAdministration($seasonID: ID!) {
    readSeasonForAdministration(seasonID: $seasonID) {
      id
      scheduleEmailActiveTime
      scheduleEmailContent
      scheduleEmailChangeContent
      cancelationEmailContent
    }
  }
`)

export const readUserOfGroupQuery = graphql(`
  query readUserOfGroup($input: ReadUserOfGroupInput) {
    readUserOfGroup(input: $input) {
      id
      firstName
      surname
      minorAge
      targetGroupMembership {
        id
        statusLabels {
          id
          title
          color
        }
      }
      userWorkload {
        userID
        date
        workload
        __typename
      }
    }
    readCurrentSeason {
      id
      userWithShiftExemption {
        id
      }
      userWithSectionLeadership {
        id
      }
    }
  }
`)

export const readCurrentSeasonControlListQuery = graphql(`
  query ReadCurrentSeasonControlList {
    readCurrentSeason {
      id
      userWithAvailabilitiesWithoutTargetGroup {
        id
        firstName
        surname
      }
      userWithSectionsWithoutTargetGroup {
        id
        firstName
        surname
      }
      userWithSlotsWithoutTargetGroup {
        id
        firstName
        surname
      }
#      userInTargetGroupWithoutSection {
#        id
#        firstName
#        surname
#      }
    }
  }
`)

export const readCurrentSeasonChangedScheduleEmailsQuery = graphql(`
  query ReadCurrentSeasonChangedScheduleEmails {
    readCurrentSeason {
      id
      userWithChangedScheduleEmails {
        id
        firstName
        surname
        email
      }
    }
  }
`)

export const readUserWithSectionsWithoutTargetGroupQuery = graphql(`
  query ReadUserWithSectionsWithoutTargetGroup {
    readCurrentSeason {
      id
      userWithSectionsWithoutTargetGroup {
        id
        firstName
        surname
      }
    }
  }
`)

export const readUserWithSlotsWithoutTargetGroupQuery = graphql(`
  query ReadUserWithSlotsWithoutTargetGroup {
    readCurrentSeason {
      id
      userWithSlotsWithoutTargetGroup {
        id
        firstName
        surname
      }
    }
  }
`)

export const readUserLogEntriesQuery = graphql(`
  query ReadUserLogEntries($input: ReadOneUserInput!) {
    readOneUser(input: $input) {
      id
      logEntries {
        id
        date
        event
        message
        additionalMessage
      }
    }
  }
`)

export const readSelfCheckinSlotsForRoleQuery = graphql(`
  query ReadSelfCheckinSlotsForRole($input: ReadSelfCheckinSlotsForRoleInput!) {
    readSelfCheckinSlotsForRole(input: $input) {
      id
      sectionRole {
        title
        autoSelfCheckoutEnabled
      }
      begin
      end
      checkinTime
      checkoutTime
    }
  }
`)

export const readConcurrentSectionSlotsQuery = graphql(`
  query ReadConcurrentSectionSlots($input: ReadConcurrentSectionSlotsInput!) {
    readConcurrentSectionSlots(input: $input) {
      date
      times {
        begin
        end
        running
        checkedIn
        started
        ended
        hasOffNow
        hasSlotLater
      }
    }
  }
`)

export const readSectionSlotsToConfirmQuery = graphql(`
  query ReadSectionSlotsToConfirm {
    readSectionSlotsToConfirm {
      id
      begin
      end
      sectionRole {
        id
        title
        section {
            id
            title
        }
      }
    }
  }
`)

export const readSectionRoleForConfirmationViewQuery = graphql(`
  query ReadSectionRoleForConfirmationView($roleID: ID!) {
    readOneSectionRole(filter: {id: {eq: $roleID}}) {
      id
      userAvailabilities {
        id
        firstName
        surname
        minorAge
        acceptedSlots {
          id
          begin
          end
          sectionRoleTitle
          sectionRoleHashID
        }
        acceptedSlotLeads {
          id
          begin
          end
          sectionRoleTitle
        }
        availabilitySlots {
          id
          begin
          end
          sectionRoleHashID
        }
      }
    }
  }
`)

export const readCateringMealsQuery = graphql(`
  query ReadCateringMeals {
    readCateringMeals {
      id
      date
      begin
      end
      title
      description
      needsVoucher
#      cateringMealDistributions {
#        id
#        timestamp
#        terminalID
#        userID
#      }
    }
  }
`)

export const readOneCateringMealQuery = graphql(`
  query ReadOneCateringMeal($input: ReadOneCateringMealInput!) {
    readOneCateringMeal(input: $input) {
      id
      date
      begin
      end
      title
      description
      cateringMealDistributions {
        id
        timestamp
        terminalID
        userID
        userName
        isAdditionalMeal
      }
    }
  }
`)

export const readCateringMealUserQuery = graphql(`
  query ReadCateringMealUser($input: ReadCateringMealUserInput!) {
    readCateringMealUser(input: $input) {
      foodPreferences
      allUser {
        id
        firstName
        surname
        nickname
        hasOffNow
        hasSlotLater
        running
        withoutCurrentSlot
        foodPreference
      }
    }
  }
`)

export const readCateringVouchersQuery = graphql(`
  query ReadMyCateringVouchers {
    readMyself {
      id
      cateringVouchers {
        id
      cateringMealDistribution {
        id
        timestamp
        additionalMeals
        isAdditionalMeal
        cateringMeal {
            id
            title
            date
            begin
            needsVoucher
          }
        }
      }
    }
  }
`)

export const readReceiptsQuery = graphql(`
  query ReadReceipts {
    readReceipts {
      recordUUID
      cipherEnvelope
    }
  }
`)

export const readEncryptionKeyQuery = graphql(`
  query ReadEncryptionKey {
    readEncryptionKey
  }
`)

export const readLogEntriesByDateQuery = graphql(`
    query ReadLogEntriesByDate($input: ReadLogEntriesByDateInput) {
        readLogEntriesByDate(input: $input) {
            id
            message
            additionalMessage
            date
            event
        }
    }
`)

export const readMessagesQuery = graphql(`
    query ReadMessages {
        readMessages {
          id
          title
          subject
          content
          summary
        }
    }
`)

export const readMessageDraftsQuery = graphql(`
    query ReadMessageDrafts {
      readMessageDrafts {
        id
        title
        description
        subject
        content
      }
    }
`)

export const readGroupsForAdministrationQuery = graphql(`
  query ReadGroupsForAdministration {
    readGroupsForAdministration {
      id
      title
      sort
    }
  }
`)

export const readGroupForAdministrationQuery = graphql(`
  query ReadGroupForAdministration($input: ReadOneGroupInput!) {
    readGroupForAdministration(input: $input) {
      id
      title
      longTitle
      imageUrl
      welcomeMessage
      footnote
      sort
      isPrivate
      isReadonly
      reuseMembership
      requiredNotificationPermissions
      newMembershipNotificationEmail
      updateMembershipNotificationEmail
      cancelMembershipNotificationEmail
      statusLabels {
        id
      }
      adminProperties {
        id
        title
        infotextHtml
        type
        sort
        shorten
        flatten
        isRequired
        isPrivate
        isReadonly
        propertyOptions {
          id
          title
          description
          limit
          sort
          disabledFlag
        }
      }
    }
  }
`)

export const readSeasonsForAdministrationQuery = graphql(`
  query ReadSeasonsForAdministration {
    readSeasonsForAdministration {
      id
      title
      shortTitle
      isLatest
      constructionBegin
      constructionEnd
      begin
      end
      dismantlingBegin
      dismantlingEnd
      scheduleEditActive
      scheduleEditLeadersActive
      scheduleActiveTime
      targetGroup {
        id
      }
      targetGroupTextRaw
      targetGroupButtonText
      targetGroupSuccessTextRaw
      scheduleAgreementTextRaw
    }
    readGroupsForAdministration {
      id
      title
    }
  }
`)

export const readSectionsOfSeasonQuery = graphql(`
  query ReadSectionsOfSeason($seasonID: ID!) {
    readSectionsOfSeason(seasonID: $seasonID) {
      id
      title
      publicAccess
      shiftExemption
      sendCancelationEmail
      organisation {
        id
        title
      }
      sectionLeaders {
        id
        firstName
        surname
      }
      editPermittedUsers {
        id
        firstName
        surname
      }
      sectionRoles {
        id
        title
        description
        publicAccess
        highPriority
        shiftExemption
        readAccessForSlotLeaders
        autoSelfCheckinEnabled
        autoSelfCheckinBegin
        autoSelfCheckinEnd
        autoSelfCheckoutEnabled
        organisation {
          id
          title
        }
      }
    }
  }
`)

export const readSeasonOrganisationsQuery = graphql(`
  query ReadSeasonOrganisations($seasonID: ID!) {
    readSeasonForAdministration(seasonID: $seasonID) {
      id
      organisations {
        id
        title
      }
    }
  }
`)

export const readSeasonLineupQuery = graphql(`
  query ReadSeasonLineup($seasonID: ID!) {
    readSeasonForAdministration(seasonID: $seasonID) {
      id
      title
      lineupStages {
        id
        title
      }
      lineupItems {
        id
        artist
        stage {
          id
          title
        }
        date
        begin
        end
      }
    }
  }
`)

export const myFestivalsQuery = graphql(`
  query MyFestivals {
    myFestivals {
      id
      title
      slug
      teaser
      location
      logoUrl
      logoBackground
      heroImageUrl
      begin
      end
    }
  }
`)

export const discoverableFestivalsQuery = graphql(`
  query DiscoverableFestivals {
    discoverableFestivals {
      id
      title
      slug
      teaser
      location
      logoUrl
      logoBackground
      heroImageUrl
      begin
      end
    }
  }
`)

export const publicFestivalsQuery = graphql(`
  query PublicFestivals {
    publicFestivals {
      id
      title
      slug
      teaser
      location
      logoUrl
      logoBackground
      heroImageUrl
      begin
      end
    }
  }
`)

export const publicFestivalQuery = graphql(`
  query PublicFestival($slug: String!) {
    publicFestival(slug: $slug) {
      id
      title
      slug
      teaser
      description
      location
      website
      logoUrl
      logoBackground
      heroImageUrl
      begin
      end
    }
  }
`)

export const readFestivalProfileQuery = graphql(`
  query ReadFestivalProfile {
    readFestivalProfile {
      id
      title
      teaser
      descriptionRaw
      location
      website
      logoBackground
      isPublic
      isJoinable
      slug
    }
  }
`)

export const readNewsQuery = graphql(`
  query ReadNews {
    readNews {
      id
      title
      category
      publishDate
      teaser
      content
      imageUrl
    }
  }
`)

export const readNewsForAdministrationQuery = graphql(`
  query ReadNewsForAdministration {
    readNewsForAdministration {
      id
      title
      category
      publishDate
      teaser
      content
      isPublished
    }
  }
`)

export const readMembershipRolesQuery = graphql(`
  query ReadMembershipRoles {
    readMembershipRoles {
      id
      title
      description
      codes
    }
  }
`)

export const readCustomerMembersQuery = graphql(`
  query ReadCustomerMembers($input: ReadCustomerMembersInput) {
    readCustomerMembers(input: $input) {
      id
      firstName
      surname
      email
      isOwner
      roleIds
    }
  }
`)
